import { isPrivilegedRole } from "@adpilot/shared";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { fetchAmazonAccounts } from "@/lib/amazon-api";
import {
  fetchCampaignCreationCapabilities,
  fetchSponsoredBrandsEligibility,
  fetchSponsoredDisplayCapabilities,
} from "@/lib/campaign-creation-api";
import { useAuthStore } from "@/stores/auth";

interface CampaignCreationQueriesInput {
  canWrite: boolean;
  workspaceId: string | null;
  selectedShopId: string | null;
}

/** Server data the wizard needs: writable Shops and the selected Shop's capabilities. */
export function useCampaignCreationQueries({
  canWrite,
  workspaceId,
  selectedShopId,
}: CampaignCreationQueriesInput) {
  // ADP-266: Sponsored Brands/Display need brand assets a demo cannot fake.
  const isDemo = useAuthStore((state) => state.user?.deploymentMode === "demo");
  const accounts = useQuery({
    queryKey: ["amazon", "accounts", workspaceId],
    queryFn: fetchAmazonAccounts,
    enabled: canWrite && Boolean(workspaceId),
  });
  const writableProfiles = useMemo(() => {
    const accountShops = accounts.data?.connections.flatMap((connection) => connection.shops) ?? [];
    return accountShops.filter(
      (shop) =>
        shop.workspaceId === workspaceId &&
        shop.isActive &&
        shop.status === "connected" &&
        isPrivilegedRole(shop.role),
    );
  }, [accounts.data, workspaceId]);

  const capabilities = useQuery({
    queryKey: ["campaign-creation", "capabilities", selectedShopId],
    queryFn: () => {
      if (!selectedShopId) throw new Error("Campaign creation requires a selected Shop");
      return fetchCampaignCreationCapabilities(selectedShopId);
    },
    enabled: canWrite && Boolean(selectedShopId),
    retry: false,
  });
  const sponsoredBrandsEligibility = useQuery({
    queryKey: ["campaign-creation", "sponsored-brands", "eligibility", selectedShopId],
    queryFn: () => {
      if (!selectedShopId) throw new Error("Sponsored Brands eligibility requires a Shop");
      return fetchSponsoredBrandsEligibility(selectedShopId);
    },
    enabled: canWrite && Boolean(selectedShopId) && !isDemo,
    retry: false,
  });
  const sponsoredDisplayCapabilities = useQuery({
    queryKey: ["campaign-creation", "sponsored-display", "capabilities", selectedShopId],
    queryFn: () => {
      if (!selectedShopId) throw new Error("Sponsored Display capability requires a Shop");
      return fetchSponsoredDisplayCapabilities(selectedShopId);
    },
    enabled: canWrite && Boolean(selectedShopId) && !isDemo,
    retry: false,
  });

  return {
    accounts,
    writableProfiles,
    capabilities,
    sponsoredBrandsEligibility,
    sponsoredDisplayCapabilities,
  };
}
